/**
 * Frontend JavaScript for ACF Display Block
 * Handles loading ACF field values in Query Loop contexts
 */

/**
 * Helper function to generate style attribute for width/height
 */
function getStyleAttribute(width, height) {
    const styles = [];
    if (width) styles.push(`width: ${width}`);
    if (height) styles.push(`height: ${height}`);

    return styles.length > 0 ? ` style="${styles.join('; ')}"` : '';
}

/**
 * Format date value with separate spans for day, month, and year
 * Returns HTML with <span>j</span><span>M</span><span>Y</span> format
 */
function formatDateWithSpans(dateValue) {
    if (!dateValue) return null;

    let date;

    // Handle different date formats
    if (typeof dateValue === 'string') {
        // Common ACF date formats: YYYYMMDD, YYYY-MM-DD, DD/MM/YYYY, MM/DD/YYYY
        if (dateValue.match(/^\d{8}$/)) {
            // YYYYMMDD format
            const year = dateValue.substring(0, 4);
            const month = dateValue.substring(4, 6);
            const day = dateValue.substring(6, 8);
            date = new Date(year, month - 1, day);
        } else {
            // Try parsing as standard date
            date = new Date(dateValue);
        }
    } else if (dateValue instanceof Date) {
        date = dateValue;
    } else {
        return null;
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
        return null;
    }

    // Format components
    const day = date.getDate(); // j format (1-31)
    const month = date.toLocaleString('default', { month: 'short' }); // M format (Jan, Feb, etc)
    const year = date.getFullYear(); // Y format (full year)

    return `<span class="acf-date-day">${day}</span><span class="acf-date-month">${month}</span><span class="acf-date-year">${year}</span>`;
}

// Function to process ACF blocks (can be called multiple times)
function processACFBlocks() {
    // Find all ACF Display blocks - use the full WordPress block class
    const acfBlocks = document.querySelectorAll('.wp-block-polarstork-acf-display[data-acf-field], .acf-display-block[data-acf-field]');


    acfBlocks.forEach((block, index) => {
        const fieldName = block.getAttribute('data-acf-field');
        const fieldType = block.getAttribute('data-field-type') || 'text';
        const fallback = block.getAttribute('data-fallback') || '';

        // Check if this block is inside a Query Loop post
        const postElement = block.closest('.wp-block-post');
        if (postElement) {
            loadQueryLoopFieldValue(block, fieldName, fieldType, fallback);
        } else {

            // Check if inside a swiper slide (which might contain Query Loop content)
            const swiperSlide = block.closest('.swiper-slide');
            if (swiperSlide) {
                loadSwiperSlideFieldValue(block, fieldName, fieldType, fallback, swiperSlide);
            } else {
                // For regular contexts, try to get the current post ID
                const postId = getPostIdFromContext();
                if (postId) {
                    loadRegularFieldValue(block, fieldName, fieldType, fallback, postId);
                } else {
                    renderFallback(block, fieldName, fallback);
                }
            }
        }
    });
}

// Initialize ACF blocks immediately
document.addEventListener('DOMContentLoaded', processACFBlocks);

// Also process ACF blocks after a delay to catch Swiper cloning
setTimeout(processACFBlocks, 1000);

// Process ACF blocks when Swiper slides change
document.addEventListener('swiperSlideChange', processACFBlocks);

// Watch for DOM changes (like Swiper cloning) and reprocess ACF blocks
const observer = new MutationObserver(function (mutations) {
    let shouldReprocess = false;
    mutations.forEach(function (mutation) {
        if (mutation.type === 'childList') {
            // Check if Swiper-related changes happened
            mutation.addedNodes.forEach(function (node) {
                if (node.nodeType === 1 && (node.classList.contains('swiper-slide') || node.querySelector('.wp-block-polarstork-acf-display'))) {
                    shouldReprocess = true;
                }
            });
        }
    });

    if (shouldReprocess) {
        setTimeout(processACFBlocks, 100); // Small delay to ensure DOM is stable
    }
});

// Start observing (ensure document.body exists)
function startObserver() {
    if (document.body) {
        observer.observe(document.body, {
            childList: true,
            subtree: true
        });
    } else {
        // Retry after DOM is ready
        setTimeout(startObserver, 100);
    }
}

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startObserver);
} else {
    startObserver();
}

/**
 * Load ACF field value for blocks inside Query Loop
 */
function loadQueryLoopFieldValue(block, fieldName, fieldType, fallback) {
    // In Query Loop, WordPress automatically provides post context
    // We can use the global post data or try to extract it from the DOM

    const postElement = block.closest('.wp-block-post');
    const postId = getPostIdFromQueryLoop(postElement);

    if (postId) {

        // Get post type from block's data attribute (set by WordPress when rendering)
        const postType = block.getAttribute('data-post-type') || 'post';

        fetchAndRenderField(block, fieldName, fieldType, fallback, postId, postType);
    } else {
        renderFallback(block, fieldName, fallback);
    }
}

/**
 * Load ACF field value for blocks in regular context
 */
function loadRegularFieldValue(block, fieldName, fieldType, fallback, postId) {
    // For regular contexts, use the current post
    const postType = getPostTypeFromContext();
    fetchAndRenderField(block, fieldName, fieldType, fallback, postId, postType);
}

/**
 * Load ACF field value for blocks inside Swiper slides (Query Loop content)
 */
function loadSwiperSlideFieldValue(block, fieldName, fieldType, fallback, swiperSlide) {

    // For Swiper slides containing Query Loop content, we need a different approach
    // Since the post IDs aren't easily accessible, we'll try to fetch all posts
    // of the detected type and match by slide index

    const allSlides = Array.from(swiperSlide.parentElement.children);
    const slideIndex = allSlides.indexOf(swiperSlide);


    // Detect post type from context
    const postType = detectPostTypeFromContext(swiperSlide);

    // Fetch posts and get the one at this index
    fetchPostsAndRender(block, fieldName, fieldType, fallback, slideIndex, postType);
}

/**
 * Fetch ACF field value from REST API and render it
 */
function fetchAndRenderField(block, fieldName, fieldType, fallback, postId, postType) {
    // Construct REST API endpoint
    const apiRoot = window.polarstork_theme_script_vars?.rest_url || '/wp-json/';
    let endpoint;

    if (postType === 'page') {
        endpoint = `${apiRoot}wp/v2/pages/${postId}`;
    } else if (postType === 'post') {
        endpoint = `${apiRoot}wp/v2/posts/${postId}`;
    } else {
        // Custom post types
        endpoint = `${apiRoot}wp/v2/${postType}/${postId}`;
    }


    fetch(endpoint)
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.json();
        })
        .then(post => {
            const fieldValue = post.acf?.[fieldName] || post.meta?.[fieldName] || '';

            if (fieldValue) {
                renderFieldValue(block, fieldValue, fieldType);
            } else {
                renderFallback(block, fieldName, fallback, fieldType);
            }
        })
        .catch(error => {
            renderFallback(block, fieldName, fallback, fieldType);
        });
}

/**
 * Render the field value based on field type
 */
function renderFieldValue(block, value, fieldType) {
    // For file fields, we need to handle empty values to show/hide buttons
    // So don't return early for file type
    if (!value) {
        return;
    }

    // Get width and height from data attributes
    const width = block.getAttribute('data-width');
    const height = block.getAttribute('data-height');
    const styleAttr = getStyleAttribute(width, height);

    switch (fieldType) {
        case 'image':
            if (typeof value === 'object' && value.url) {
                block.innerHTML = `<img src="${value.url}" alt="${value.alt || ''}"${styleAttr} />`;
            } else if (typeof value === 'string' && value.match(/\.(jpg|jpeg|png|gif|webp)$/i)) {
                block.innerHTML = `<img src="${value}" alt=""${styleAttr} />`;
            } else {
                block.textContent = value;
            }
            break;

        case 'link':
            if (typeof value === 'object' && value.url) {
                const target = value.target || '_self';
                block.innerHTML = `<a href="${value.url}" target="${target}">${value.title || value.url}</a>`;
            } else if (typeof value === 'string' && value.startsWith('http')) {
                block.innerHTML = `<a href="${value}">${value}</a>`;
            } else {
                block.textContent = value;
            }
            break;

        case 'email':
            block.innerHTML = `<a href="mailto:${value}">${value}</a>`;
            break;

        case 'url':
            block.innerHTML = `<a href="${value}" target="_blank" rel="noopener noreferrer">${value}</a>`;
            break;

        case 'textarea':
            block.innerHTML = value.replace(/\n/g, '<br>');
            break;

        case 'date_picker':
        case 'date_time_picker':
        case 'time_picker':
        case 'date':
            // Format date with separate spans for day, month, and year
            const dateValue = formatDateWithSpans(value);
            if (dateValue) {
                block.innerHTML = dateValue;
            } else {
                block.textContent = value;
            }
            break;

        case 'file':
            // Handle ACF file fields
            // Get custom display text from block attributes first
            const displayText = block.getAttribute('data-display-text') || 'File Available';
            const showOnlyWhenHasValue = block.getAttribute('data-show-only-when-has-value') === 'true';
            const customStyle = block.getAttribute('data-custom-style') || '';

            // Check if file exists - handle false, null, empty string, empty object
            let hasFile = false;

            if (value) {
                if (typeof value === 'object' && value !== null) {
                    // Check if object has URL property
                    hasFile = !!(value.url);
                } else if (typeof value === 'string') {
                    // String must be non-empty and not "false"
                    hasFile = value.trim() !== '' && value.toLowerCase() !== 'false';
                }
            }


            // Show parent button if it was hidden (in case it's being re-rendered)
            const parentButton = block.closest('.wp-block-button');
            if (parentButton) {
                parentButton.style.display = '';
            }

            if (hasFile) {
                if (typeof value === 'object' && value.url) {
                    const fileName = displayText || value.filename || value.title;
                    block.innerHTML = `<a href="${value.url}" target="_blank" style="${customStyle}">${fileName}</a>`;
                } else if (typeof value === 'string' && value.startsWith('http')) {
                    // Direct URL string
                    block.innerHTML = `<a href="${value}" target="_blank" style="${customStyle}">${displayText}</a>`;
                } else {
                    // Show display text for any truthy value
                    block.innerHTML = `<span style="${customStyle}">${displayText}</span>`;
                }
            }
            break;

        case 'oembed':
            // For oEmbed, apply width/height to the container
            if (width || height) {
                block.style.cssText = `${width ? `width: ${width};` : ''}${height ? `height: ${height};` : ''}`;
            }

            // oEmbed content should be processed by WordPress on the backend
            // Here we just display the raw URL or processed HTML
            if (typeof value === 'string') {
                if (value.includes('<iframe') || value.includes('<embed')) {
                    // Already processed oEmbed HTML
                    block.innerHTML = value;
                } else {
                    // Raw URL - let WordPress handle it or show as link
                    block.innerHTML = `<a href="${value}" target="_blank" rel="noopener noreferrer">${value}</a>`;
                }
            } else {
                block.innerHTML = value;
            }
            break;

        default: // text, number, etc.
            // Try multiple methods to set the content
            block.textContent = value;
            block.innerHTML = value; // Also try innerHTML in case textContent is blocked

            // Force the content with setAttribute as well
            block.setAttribute('data-rendered-value', value);
            break;
    }

}

/**
 * Render fallback text when field is empty
 */
function renderFallback(block, fieldName, fallback, fieldType) {
    const fallbackText = fallback || '';
    if (fallbackText) {
        block.textContent = fallbackText;
        block.innerHTML = fallbackText;
    }
    if (fieldType == 'file' && !fallbackText) {
        block.parentElement.style.display = 'none';
    }



}

/**
 * Try to get post ID from Query Loop context
 */
function getPostIdFromQueryLoop(postElement) {

    // Try different methods to get the post ID
    if (postElement) {
        // Method 1: Check for data attributes
        const postId = postElement.getAttribute('data-post-id');
        if (postId) {
            return postId;
        }

        // Method 2: Check for post class names (WordPress often adds post-123 classes)
        const classes = postElement.className.match(/post-(\d+)/);
        if (classes && classes[1]) {
            return classes[1];
        }

        // Method 3: Look for post title or content that might have post ID
        const postTitle = postElement.querySelector('.wp-block-post-title');
        // Method 4: For testimonials, we need a different approach - get all testimonials and match by index
        const allPosts = document.querySelectorAll('.wp-block-post');
        const postIndex = Array.from(allPosts).indexOf(postElement);
        if (postIndex >= 0) {
            // We'll need to get the post ID another way - maybe from the Query Loop attributes
            return getPostIdFromQueryIndex(postIndex);
        }

        // Method 5: Look for hidden input or other indicators
        const hiddenInput = postElement.querySelector('input[name="post_id"]');
        if (hiddenInput) {
            return hiddenInput.value;
        }
    }

    return null;
}

/**
 * Get post ID from Query Loop by index (fallback method)
 */
function getPostIdFromQueryIndex(index) {
    // This is a fallback - in a real Query Loop, we'd need to fetch the posts
    // For now, we'll make a simplified assumption or try to get it from the query itself

    // Try to find the Query Loop block and its attributes
    const queryBlock = document.querySelector('.wp-block-query');
    if (queryBlock) {
        // In a real implementation, we might need to make an AJAX call to get the posts
        // or have the server render the post IDs as data attributes
    }

    return null;
}

/**
 * Get current post ID from page context
 */
function getPostIdFromContext() {
    // Try to get from global WordPress variables
    if (window.wp && window.wp.data) {
        const editor = window.wp.data.select('core/editor');
        if (editor) {
            return editor.getCurrentPostId();
        }
    }

    // Fallback: try to get from body classes or meta tags
    const bodyClasses = document.body.className.match(/postid-(\d+)/);
    if (bodyClasses && bodyClasses[1]) {
        return bodyClasses[1];
    }

    return null;
}

/**
 * Get current post type from context
 */
function getPostTypeFromContext() {
    // Try to get from global WordPress variables
    if (window.wp && window.wp.data) {
        const editor = window.wp.data.select('core/editor');
        if (editor) {
            return editor.getCurrentPostType();
        }
    }

    // Fallback: try to get from body classes
    const bodyClasses = document.body.className.match(/post-type-(\w+)/);
    if (bodyClasses && bodyClasses[1]) {
        return bodyClasses[1];
    }

    return 'post'; // Default fallback
}

/**
 * Detect post type from Query Loop context or surrounding elements
 */
function detectPostTypeFromContext(postElement) {
    // Method 1: Check for data attribute on post element
    if (postElement) {
        const postType = postElement.getAttribute('data-post-type');
        if (postType) {
            return postType;
        }
    }

    // Method 2: Check the Query Loop block for post type
    const queryBlock = postElement?.closest('.wp-block-query');
    if (queryBlock) {
        // Look for post type in classes or data attributes
        const classes = queryBlock.className.split(' ');
        for (const cls of classes) {
            const match = cls.match(/query-post-type-(\w+)/);
            if (match) {
                return match[1];
            }
        }

        const postTypeAttr = queryBlock.getAttribute('data-query-post-type');
        if (postTypeAttr) {
            return postTypeAttr;
        }
    }

    // Method 3: Check body classes for single post type pages
    const bodyClasses = document.body.className.match(/post-type-(\w+)/);
    if (bodyClasses && bodyClasses[1]) {
        return bodyClasses[1];
    }

    // Method 4: Check if we're in a specific archive
    const archiveMatch = document.body.className.match(/post-type-archive-(\w+)/);
    if (archiveMatch && archiveMatch[1]) {
        return archiveMatch[1];
    }

    // Default fallback
    return 'posts';
}

/**
 * Fetch posts and render field for specific index
 */
function fetchPostsAndRender(block, fieldName, fieldType, fallback, slideIndex, postType) {
    const apiRoot = window.polarstork_theme_script_vars?.rest_url || '/wp-json/';

    // Determine correct endpoint based on post type
    let endpoint;
    if (postType === 'post') {
        endpoint = `${apiRoot}wp/v2/posts?per_page=100&orderby=date&order=desc`;
    } else if (postType === 'page') {
        endpoint = `${apiRoot}wp/v2/pages?per_page=100&orderby=date&order=desc`;
    } else {
        // Custom post types
        endpoint = `${apiRoot}wp/v2/${postType}?per_page=100&orderby=date&order=desc`;
    }


    fetch(endpoint)
        .then(response => {
            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }
            return response.json();
        })
        .then(posts => {

            if (posts[slideIndex]) {
                const post = posts[slideIndex];

                const fieldValue = post.acf?.[fieldName] || post.meta?.[fieldName] || '';

                if (fieldValue) {
                    renderFieldValue(block, fieldValue, fieldType);
                } else {
                    renderFallback(block, fieldName, fallback);
                }
            } else {
                renderFallback(block, fieldName, fallback, fieldType);
            }
        })
        .catch(error => {
            renderFallback(block, fieldName, fallback, fieldType);
        });
}